// [ai] What every catalog component shares: its data through the host it was handed (useAtlas — the same contract a sandboxed
// widget has, so a catalog widget never reaches past the SDK), cached under the query client's ["q", name, params] key so the
// SSE invalidations refresh dashboards like pages; the `query` object of view.json (`{name, params}`) or the widget's default;
// dot paths into an answer; opening a row through the host (a page, a rec:/note: link — never a raw navigation).
import { useQuery } from "@tanstack/react-query";
import { queryKey } from "../../api/query";
import { useAtlas } from "../../sdk/react";
import type { AtlasHost } from "../../sdk/types";
import type { WidgetSpec } from "../types";

export type Params = Record<string, unknown>;
export type QuerySpec = { name: string; params: Params };
export type WidgetProps = { spec: WidgetSpec };

export function querySpec(spec: WidgetSpec, name: string, params: Params = {}): QuerySpec {
  const q = spec.query as { name?: unknown; params?: unknown } | undefined;
  if (q && typeof q.name === "string") return { name: q.name, params: q.params && typeof q.params === "object" && !Array.isArray(q.params) ? { ...params, ...(q.params as Params) } : params };
  return { name, params };
}
export function useHostQuery<T>(q: QuerySpec, enabled = true) {
  const atlas = useAtlas();
  return useQuery<T, Error>({ queryKey: queryKey(q.name, q.params), queryFn: () => atlas.query<T>(q.name, q.params), enabled, retry: false });
}
/** `a.b.0` into an answer; "" is the answer itself. */
export function get(data: unknown, path: string | undefined): unknown {
  if (!path) return data;
  return path.split(".").reduce<unknown>((o, k) => (o && typeof o === "object" ? (o as Record<string, unknown>)[k] : undefined), data);
}
/** The list a widget shows: props.path, else `items`, else the first list in the answer. */
export function rows(data: unknown, path: string | undefined): Record<string, unknown>[] {
  const v = get(data, path ?? "items");
  if (Array.isArray(v)) return v as Record<string, unknown>[];
  if (!path && data && typeof data === "object") { for (const x of Object.values(data as Record<string, unknown>)) if (Array.isArray(x)) return x as Record<string, unknown>[]; }
  return [];
}
/** `/play/{id}` with the row's fields, or the field named. */
export function hrefOf(row: Record<string, unknown>, tpl: string | undefined): string | null {
  if (!tpl) { const h = row.href; return typeof h === "string" ? h : null; }
  if (!tpl.includes("{")) { const h = row[tpl]; return typeof h === "string" ? h : null; }
  let missing = false; const out = tpl.replace(/\{([\w.]+)\}/g, (_, k: string) => { const v = get(row, k); if (v == null) missing = true; return encodeURIComponent(String(v ?? "")); }); return missing ? null : out;
}
export const openVia = (host: AtlasHost, href: string | null) => (e: React.MouseEvent) => { if (!href || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; e.preventDefault(); try { host.open(href); } catch { /* not a page of this app: the plain link stays */ } };
export const text = (v: unknown): string => (v == null ? "" : typeof v === "string" ? v : typeof v === "number" ? String(v) : JSON.stringify(v));
export function Wait() { return <p className="at-widget-wait">Loading…</p>; }
